import React from 'react';
import { useSnackbar } from 'notistack';

import { DataAPIContext, GET_ONE } from '../api';

/**
 * Hook for "select all matching records" functionality.
 *
 * Works alongside RecordListPro without modifying it. Calls the generic
 * select_all_ids endpoint with the same filters used by the list view.
 *
 * `select_all_ids` returns at most MAX_SELECT_ALL (5,000) ids, because a bulk
 * action that must name its records — batch delete, say — has to materialize
 * them. Export does not: it re-sends the filters and the server resolves the
 * whole set, so no cap applies. Pass `allMatchingCapable` on a grid whose bulk
 * actions all work that way and the cap stops being reported as a limit, since
 * for that grid it is not one. Leave it off and the warning stands.
 *
 * `selectedIds` is still the capped list either way — it is what draws the
 * checkmarks and what an id-based bulk action would use.
 *
 * Usage:
 *   const { selectedIds, setSelectedIds, selectAll, deselectAll,
 *           selectAllMeta, loading } = useSelectAll({
 *       resource: 'tour_user',
 *       apiFilters: [...],
 *       query: searchText,
 *       extraParams: { tour_id: tripId },
 *   });
 *
 *   // Pass to RecordListPro:
 *   <RecordListPro
 *       rowSelectionModel={selectedIds}
 *       onRowSelectionModelChange={setSelectedIds}
 *       keepNonExistentRowsSelected
 *   />
 *
 *   // Render banner:
 *   <SelectAllBanner {...selectAllProps} />
 */
const useSelectAll = ({ resource, apiFilters, query, extraParams, allMatchingCapable }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();

    const [selectedIds, _setSelectedIds] = React.useState([]);
    const [selectAllMeta, setSelectAllMeta] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const selectAllInProgressRef = React.useRef(false);

    // Wrapped setter: clears selectAllMeta on manual checkbox changes,
    // but ignores the DataGrid sync-back after selectAll().
    const setSelectedIds = React.useCallback((ids) => {
        _setSelectedIds(ids);
        if (selectAllInProgressRef.current) {
            // This is the sync-back from DataGrid after selectAll() set IDs.
            // Don't clear meta — it was just set.
            selectAllInProgressRef.current = false;
        } else {
            // Manual checkbox change — no longer a "select all" state.
            setSelectAllMeta(null);
        }
    }, []);

    const selectAll = React.useCallback(async () => {
        setLoading(true);
        try {
            const filtersJson = JSON.stringify(apiFilters || []);
            let url = `select_all_ids/?filters=${encodeURIComponent(filtersJson)}`;
            if (query) {
                url += `&query=${encodeURIComponent(query)}`;
            }
            if (extraParams) {
                Object.entries(extraParams).forEach(([k, v]) => {
                    if (v != null) {
                        url += `&${k}=${encodeURIComponent(v)}`;
                    }
                });
            }
            const res = await dataAPI(GET_ONE, resource, { urlSuffix: url });
            // An unhandled backend exception comes back as an HTML error page
            // with status 200, so check for the ids array itself. Without it
            // selectedIds became undefined and the grid crashed the page (CP-8422).
            if (res && res.statusCode === 200 && Array.isArray(res.data?.ids)) {
                selectAllInProgressRef.current = true;
                _setSelectedIds(res.data.ids);
                setSelectAllMeta({
                    totalCount: res.data.total_count,
                    limit: res.data.limit,
                    // When the consumer can act on the FILTERS rather than on a
                    // materialized id list, the cap below bounds nothing the
                    // user asked for — see allMatchingCapable.
                    allMatchingCapable: !!allMatchingCapable,
                });
                if (!allMatchingCapable && res.data.total_count > res.data.limit) {
                    enqueueSnackbar(
                        `Selected ${res.data.limit} of ${res.data.total_count} matching records (maximum)`,
                        { variant: 'warning', autoHideDuration: 6000 },
                    );
                }
            } else {
                enqueueSnackbar('Error selecting all records', { variant: 'error' });
            }
        } catch (error) {
            console.error('useSelectAll error:', error);
            enqueueSnackbar('Error selecting all records', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [resource, apiFilters, query, extraParams, allMatchingCapable, dataAPI, enqueueSnackbar]);

    const deselectAll = React.useCallback(() => {
        _setSelectedIds([]);
        setSelectAllMeta(null);
    }, []);

    // Clear selection when filters or query change
    const filtersKey = JSON.stringify(apiFilters || []);
    React.useEffect(() => {
        deselectAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey, query]);

    return {
        selectedIds,
        setSelectedIds,
        selectAll,
        deselectAll,
        selectAllMeta,
        loading,
        isAllSelected: selectAllMeta !== null,
    };
};

export default useSelectAll;
